import React from 'react';
// no i18n hooks here; labels are passed pre-translated
import { BookingDetailsData } from '../bookingsSlice';

export interface BasicBookingLabels {
  bookingNumber: string;
  keyCode: string;
  bookingType: string;
  created: string;
  modifiedConfirmed: string;
  validUntil: string;
  clerk: string;
  travelAgency: string;
  interhome: string;
  interhomeYllaksenmatkailu: string;
  agent: string;
}

interface BasicBookingDataProps {
  item: BookingDetailsData;
  labels: BasicBookingLabels;
  bookingTypeText: string;
  isTravelAgencyUser?: boolean;
}
// Renders the core "basic booking data" row used in BookingDetails both for single and multi-booking cases.
// This component intentionally receives already-translated label strings to accommodate differing
// translation key paths used in the two contexts (with and without ".labels").
export const BasicBookingData: React.FC<BasicBookingDataProps> = ({
  item,
  labels,
  bookingTypeText,
  isTravelAgencyUser = false,
}) => {
  return (
    <div className="d-flex flex-column flex-lg-row gap-24 justify-content-between mb-32">
      <div className="d-flex flex-column flex-lg-row gap-8 gap-lg-40">
        <div>
          <span className="fw-bold small">{labels.bookingNumber}</span>
          <br />
          {item.bookingNumber}
          {item.externalBookingIdsDto?.externalRestApiBookingId && (
            <>
              <br />
              {item.externalBookingIdsDto.externalRestApiBookingId}
            </>
          )}
        </div>
        {!isTravelAgencyUser && (
          <div>
            <span className="fw-bold small">{labels.keyCode}:</span>
            {item.keyBox !== '' && (
              <span>
                <br />
                {item.keyBox}
              </span>
            )}
          </div>
        )}
        <div>
          <span className="fw-bold small">{labels.bookingType}</span>
          <br />
          {bookingTypeText}
          {/* External booking source tags (if any) */}
          {item.externalBookingIdsDto?.externalBookingSource ===
            'TRAVEL_AGENCY' && (
            <span className="small">{labels.travelAgency}</span>
          )}
          {item.externalBookingIdsDto?.externalBookingSource ===
            'INTERHOME' && <span className="small">{labels.interhome}</span>}
          {item.externalBookingIdsDto?.externalBookingSource ===
            'INTERHOME_YLLAKSENMATKAILU' && (
            <span className="small">{labels.interhomeYllaksenmatkailu}</span>
          )}
        </div>
        {/* Basket status intentionally omitted (commented in original). */}
        <div>
          <span className="fw-bold small">{labels.created}</span>
          <br />
          {item.createdAt
            ? new Date(Date.parse(item.createdAt)).toLocaleString('fi-FI', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            : ''}{' '}
        </div>
        <div>
          <span className="fw-bold small">{labels.modifiedConfirmed}</span>
          <br />
          {item.updatedAt
            ? new Date(Date.parse(item.updatedAt)).toLocaleString('fi-FI', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            : ''}{' '}
        </div>
        <div>
          <span className="fw-bold small">{labels.validUntil}:</span>
          <br />
          {item.preliminaryBookingDueDate
            ? new Date(
                Date.parse(item.preliminaryBookingDueDate),
              ).toLocaleString('fi-FI', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            : ''}{' '}
        </div>
        <div>
          <span className="fw-bold small">{labels.clerk}</span>
          <br />
          {item.authorEmail !== null && item.authorEmail !== undefined && (
            <div>{item.authorEmail}</div>
          )}{' '}
          {item.authorFirstName !== null &&
            item.authorFirstName !== undefined && (
              <div>
                {item.authorFirstName}
                {item.authorLastName !== null &&
                item.authorLastName !== undefined
                  ? ` ${item.authorLastName}`
                  : ''}
              </div>
            )}{' '}
        </div>
      </div>
    </div>
  );
};

export default BasicBookingData;
